import { v4 as uuidv4 } from 'uuid';
import { WorkflowConnection, WorkflowNode } from '../../types/workflow';
import {
  ConversationNodeClass,
  WorkflowDefinitionV1,
  WorkflowNodeDefinition,
  WorkflowTransitionDefinition,
} from '../../types/workflow-spec';

const inferNodeClass = (node: WorkflowNode): ConversationNodeClass => {
  if (node.nodeClass) return node.nodeClass;
  if (node.data?.nodeClass) return node.data.nodeClass as ConversationNodeClass;
  if (node.data?.nodeKind === 'agent') return 'conversation';
  if (node.type === 'trigger') return 'trigger';
  if (node.type === 'condition') return 'router';
  if (node.type === 'delay') return 'end';
  return 'action';
};

const toWorkflowNodeDefinition = (node: WorkflowNode): WorkflowNodeDefinition => {
  const nodeClass = inferNodeClass(node);
  const common = {
    id: node.id,
    name: node.name,
    title: node.title ?? node.name,
    summary: node.summary ?? String(node.data?.description ?? ''),
    nodeClass,
    position: node.position,
    inputs: node.inputs,
    outputs: node.outputs,
    policy: node.data?.policy,
    canvasType: node.type,
  };

  switch (nodeClass) {
    case 'trigger':
      return {
        ...common,
        kind: 'trigger',
        nodeClass: 'trigger',
        triggerType: (node.data?.triggerType as 'inbound-call' | 'outbound-call' | 'webhook' | 'manual' | 'whatsapp') ?? 'manual',
        agentId: String(node.data?.agentId ?? ''),
        agentVersion: String(node.data?.agentVersion ?? '1'),
        config: node.data ?? {},
      };
    case 'conversation':
      return {
        ...common,
        kind: 'conversation',
        nodeClass: 'conversation',
        agentId: String(node.data?.agentId ?? ''),
        agentVersion: String(node.data?.agentVersion ?? '1.0.0'),
        config: node.data ?? {},
      };
    case 'router':
      return {
        ...common,
        kind: 'router',
        nodeClass: 'router',
        expression: node.data?.expression ? String(node.data.expression) : undefined,
        config: node.data ?? {},
      };
    case 'human-handoff':
      return {
        ...common,
        kind: 'human-handoff',
        nodeClass: 'human-handoff',
        queueId: node.data?.queueId ? String(node.data.queueId) : undefined,
        teamId: node.data?.teamId ? String(node.data.teamId) : undefined,
        config: node.data ?? {},
      };
    case 'end':
      return {
        ...common,
        kind: 'end',
        nodeClass: 'end',
        result: (node.data?.result as 'success' | 'failure') ?? 'success',
      };
    default:
      return {
        ...common,
        kind: 'action',
        nodeClass: 'action',
        agentId: String(node.data?.agentId ?? ''),
        agentVersion: String(node.data?.agentVersion ?? '1.0.0'),
        config: node.data ?? {},
      };
  }
};

const toWorkflowTransitionDefinition = (connection: WorkflowConnection): WorkflowTransitionDefinition => ({
  id: connection.id || uuidv4(),
  sourceNodeId: connection.sourceNodeId,
  sourcePortId: connection.sourceOutputId,
  targetNodeId: connection.targetNodeId,
  targetPortId: connection.targetInputId,
  label: connection.label?.trim() || 'Next',
  conditionType: connection.conditionType ?? 'custom',
  priority: connection.priority,
  isLoopback: connection.isLoopback,
  metadata: connection.metadata,
});

export const buildWorkflowDefinition = (params: {
  id?: string;
  name: string;
  description?: string;
  nodes: WorkflowNode[];
  connections: WorkflowConnection[];
  status?: WorkflowDefinitionV1['status'];
}): WorkflowDefinitionV1 => {
  const timestamp = new Date().toISOString();

  return {
    specVersion: 'v1',
    id: params.id ?? uuidv4(),
    name: params.name,
    description: params.description,
    status: params.status ?? 'draft',
    nodes: params.nodes.map(toWorkflowNodeDefinition),
    transitions: params.connections.map(toWorkflowTransitionDefinition),
    createdAt: timestamp,
    updatedAt: timestamp,
  };
};

